import { useState, useMemo } from 'react';
import { Grid, List, Search, Star, Clock, Filter, X } from 'lucide-react';
import { useAppStore } from '../../store/appStore';
import { App, AppCategory } from '../../types';
import { getCategoryDisplayName } from '../../utils/categoryMapping';
import { AppIcon } from '../icons/AppIcons';

interface AppGridProps {
  searchTerm?: string;
  selectedCategory?: AppCategory | 'All';
}

export default function AppGrid({ searchTerm = '', selectedCategory = 'All' }: AppGridProps) {
  const {
    apps,
    favoriteAppIds,
    recentAppIds,
    preferences,
    setViewMode,
    toggleFavorite,
    addRecentApp,
    addActivity
  } = useAppStore();

  const [activeTab, setActiveTab] = useState<'all' | 'favorites' | 'recent'>('all');
  const [localSearchTerm, setLocalSearchTerm] = useState('');

  const effectiveSearchTerm = searchTerm || localSearchTerm;

  // Filter apps based on search and category
  const filteredApps = useMemo(() => {
    let filtered = apps;

    // Apply tab filter
    if (activeTab === 'favorites') {
      filtered = filtered.filter((app) => favoriteAppIds.includes(app.id));
    } else if (activeTab === 'recent') {
      filtered = apps.filter((app) => recentAppIds.includes(app.id));
      // Sort by recent order
      filtered = filtered.sort((a, b) => {
        const aIndex = recentAppIds.indexOf(a.id);
        const bIndex = recentAppIds.indexOf(b.id);
        return aIndex - bIndex;
      });
    }

    // Apply category filter
    if (selectedCategory !== 'All') {
      filtered = filtered.filter((app) => app.category === selectedCategory);
    }

    // Apply search filter
    if (effectiveSearchTerm) {
      const searchLower = effectiveSearchTerm.toLowerCase();
      filtered = filtered.filter((app) => {
        const matchesName = app.name.toLowerCase().includes(searchLower);
        const matchesDescription = app.description.toLowerCase().includes(searchLower);
        const matchesTags = app.tags?.some((tag) => tag.toLowerCase().includes(searchLower));
        return matchesName || matchesDescription || matchesTags;
      });
    }

    return filtered;
  }, [apps, activeTab, favoriteAppIds, recentAppIds, selectedCategory, effectiveSearchTerm]);

  // Group apps by category for grid view
  const appsByCategory = useMemo(() => {
    const grouped = new Map<AppCategory | 'pinned', App[]>();

    // Add pinned/favorite apps first
    if (activeTab === 'all' && favoriteAppIds.length > 0) {
      const pinnedApps = filteredApps.filter((app) => favoriteAppIds.includes(app.id));
      if (pinnedApps.length > 0) {
        grouped.set('pinned', pinnedApps);
      }
    }

    filteredApps.forEach((app) => {
      if (!grouped.has(app.category)) {
        grouped.set(app.category, []);
      }
      grouped.get(app.category)!.push(app);
    });

    return grouped;
  }, [filteredApps, activeTab, favoriteAppIds]);

  const handleLaunchApp = (app: App) => {
    addRecentApp(app.id);
    addActivity({
      action: 'Launched',
      appName: app.name,
      icon: app.icon
    });
    window.open(app.url, '_blank');
  };

  const handleToggleFavorite = (e: React.MouseEvent, appId: string) => {
    e.stopPropagation();
    toggleFavorite(appId);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'development':
        return 'bg-blue-500';
      case 'maintenance':
        return 'bg-yellow-500';
      case 'deprecated':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const AppCard = ({ app }: { app: App }) => {
    const isFavorite = favoriteAppIds.includes(app.id);

    return (
      <div
        onClick={() => handleLaunchApp(app)}
        className="group relative bg-[#141a16] border rounded-lg p-6 transition-all duration-200 cursor-pointer"
        style={{
          borderColor: 'rgba(168, 180, 164, 0.3)',
          borderRadius: '8px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'rgba(168, 180, 164, 0.6)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'rgba(168, 180, 164, 0.3)';
        }}
      >
        {/* Favorite Button */}
        <div className="absolute top-4 right-4">
          <button
            onClick={(e) => handleToggleFavorite(e, app.id)}
            className={`p-1.5 rounded-lg transition-colors ${
              isFavorite
                ? 'bg-recipe-accent text-white'
                : 'bg-recipe-bg text-recipe-text-muted hover:bg-recipe-accent/20'
            }`}
          >
            <Star className="w-4 h-4" fill={isFavorite ? 'currentColor' : 'none'} />
          </button>
        </div>

        {/* Icon */}
        <div className="w-12 h-12 flex items-center justify-center bg-recipe-accent/10 rounded-lg mb-4">
          <AppIcon name={app.icon} size={28} className="text-recipe-accent" />
        </div>

        {/* Content */}
        <h3 className="text-lg font-semibold text-recipe-text mb-2 pr-8 font-display">{app.name}</h3>

        {/* Status Badge */}
        {app.status === 'active' && (
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-3 rounded-md status-connected" style={{
            backgroundColor: 'rgba(74, 222, 128, 0.1)',
            border: '1px solid rgba(74, 222, 128, 0.3)',
            color: '#4ade80'
          }}>
            <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
            <span className="text-xs font-medium">Active</span>
          </div>
        )}

        <p className="text-sm text-recipe-text-muted mb-4 line-clamp-2">{app.description}</p>

        {/* Feature Tags */}
        {app.tags && app.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {app.tags.slice(0, 4).map((tag) => (
              <span
                key={tag}
                className="feature-tag px-2.5 py-1 text-xs rounded-md transition-colors"
                style={{
                  border: '1px solid rgba(168, 180, 164, 0.3)',
                  color: '#a8b4a4',
                  backgroundColor: 'transparent'
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between text-xs text-recipe-text-muted/60 mt-auto pt-2 border-t border-recipe-border/30">
          <span className="truncate max-w-[180px]">{app.url.replace('https://', '')}</span>
          {app.version && <span className="text-recipe-text-muted/40">v{app.version}</span>}
        </div>
      </div>
    );
  };

  const AppListItem = ({ app }: { app: App }) => {
    const isFavorite = favoriteAppIds.includes(app.id);

    return (
      <div
        onClick={() => handleLaunchApp(app)}
        className="group bg-recipe-card border border-recipe-border rounded-lg p-4 hover:border-recipe-accent hover:shadow-md transition-all duration-200 cursor-pointer"
      >
        <div className="flex items-center gap-4">
          {/* Icon */}
          <div className="w-10 h-10 flex items-center justify-center bg-recipe-accent/10 rounded-lg flex-shrink-0">
            <AppIcon name={app.icon} size={24} className="text-recipe-accent" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-base font-semibold text-recipe-text truncate font-display">{app.name}</h3>
              {app.version && (
                <span className="text-xs text-recipe-text-muted">v{app.version}</span>
              )}
            </div>
            <p className="text-sm text-recipe-text-muted truncate">{app.description}</p>
          </div>

          {/* Status & Actions */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className={`w-2 h-2 rounded-full ${getStatusColor(app.status)}`} />
            <button
              onClick={(e) => handleToggleFavorite(e, app.id)}
              className={`p-2 rounded-lg transition-colors ${
                isFavorite
                  ? 'bg-recipe-accent text-white'
                  : 'bg-recipe-bg text-recipe-text-muted hover:bg-recipe-accent/20'
              }`}
            >
              <Star className="w-4 h-4" fill={isFavorite ? 'currentColor' : 'none'} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        {/* Tabs */}
        <div className="flex items-center gap-2 bg-recipe-card border border-recipe-border rounded-lg p-1">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'all'
                ? 'bg-recipe-accent text-white'
                : 'text-recipe-text-muted hover:text-recipe-text'
            }`}
          >
            All Apps
          </button>
          <button
            onClick={() => setActiveTab('favorites')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'favorites'
                ? 'bg-recipe-accent text-white'
                : 'text-recipe-text-muted hover:text-recipe-text'
            }`}
          >
            <Star className="w-4 h-4" />
            Favorites
            {favoriteAppIds.length > 0 && (
              <span className="px-1.5 py-0.5 bg-recipe-accent/20 rounded text-xs">
                {favoriteAppIds.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('recent')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'recent'
                ? 'bg-recipe-accent text-white'
                : 'text-recipe-text-muted hover:text-recipe-text'
            }`}
          >
            <Clock className="w-4 h-4" />
            Recent
          </button>
        </div>

        {/* View Toggle & Search */}
        <div className="flex items-center gap-3">
          {/* Local Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-recipe-text-muted" />
            <input
              type="text"
              value={localSearchTerm}
              onChange={(e) => setLocalSearchTerm(e.target.value)}
              placeholder="Filter apps..."
              className="pl-10 pr-10 py-2 bg-recipe-card border border-recipe-border rounded-lg text-sm text-recipe-text placeholder-recipe-text-muted focus:outline-none focus:ring-2 focus:ring-recipe-accent/50 w-64"
            />
            {localSearchTerm && (
              <button
                onClick={() => setLocalSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-recipe-text-muted hover:text-recipe-text"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center gap-1 bg-recipe-card border border-recipe-border rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded transition-colors ${
                preferences.viewMode === 'grid'
                  ? 'bg-recipe-accent text-white'
                  : 'text-recipe-text-muted hover:text-recipe-text'
              }`}
              title="Grid view"
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded transition-colors ${
                preferences.viewMode === 'list'
                  ? 'bg-recipe-accent text-white'
                  : 'text-recipe-text-muted hover:text-recipe-text'
              }`}
              title="List view"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Apps Display */}
      {filteredApps.length === 0 ? (
        <div className="text-center py-16">
          <Filter className="w-12 h-12 text-recipe-text-muted mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-recipe-text mb-2">No apps found</h3>
          <p className="text-recipe-text-muted">
            Try adjusting your filters or search term
          </p>
        </div>
      ) : preferences.viewMode === 'grid' ? (
        <div className="space-y-8">
          {selectedCategory === 'All' && activeTab === 'all' ? (
            // Grouped by category
            Array.from(appsByCategory.entries()).map(([category, categoryApps]) => (
              <div key={category} className="space-y-4">
                <h2 className="text-xl font-bold text-recipe-text flex items-center gap-3 font-display">
                  <span className="section-number">
                    {categoryApps.length}
                  </span>
                  {category === 'pinned' ? 'Pinned Apps' : getCategoryDisplayName(category)}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categoryApps.map((app) => (
                    <AppCard key={app.id} app={app} />
                  ))}
                </div>
              </div>
            ))
          ) : (
            // Flat grid
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredApps.map((app) => (
                <AppCard key={app.id} app={app} />
              ))}
            </div>
          )}
        </div>
      ) : (
        // List view
        <div className="space-y-3">
          {filteredApps.map((app) => (
            <AppListItem key={app.id} app={app} />
          ))}
        </div>
      )}
    </div>
  );
}
